// Configuración de TTL (Time To Live) para diferentes tipos de datos
export const CACHE_CONFIG = {
  // Available Exercises - Cambian poco frecuentemente
  availableExercises: 2 * 60 * 1000,   // 2 minutos (ETag refresh on expiry)

  // User Profile - Cambia ocasionalmente (estadísticas)
  userProfile: 10 * 60 * 1000,         // 10 minutos

  // User Submissions - Se actualiza frecuentemente
  mySubmissions: 2 * 60 * 1000,        // 2 minutos

  // Exercise Rankings - Se actualizan al resolver; TTL corto para verlos rápido
  exerciseRankings: 1 * 60 * 1000,     // 1 minuto

  // Admin Metrics - Aggregaciones costosas; no necesitan tiempo real
  metrics: 5 * 60 * 1000,              // 5 minutos

  // Admin Guides - Cambian solo por acciones explícitas del admin
  adminGuides: 5 * 60 * 1000,          // 5 minutos

  // Admin Settings - Cambian raramente
  adminSettings: 10 * 60 * 1000,       // 10 minutos

  // Public Settings (GitHub URL, etc.) 
  publicSettings: 30 * 60 * 1000,      // 30 minutos

  // Exercise Submission Results — persisten 10 min para re-ver resultados al volver
  submissionResult: 10 * 60 * 1000,  // 10 minutos
} as const;

// Claves para identificar diferentes tipos de caché
export const CACHE_KEYS = {
  availableExercises: 'exercises:available',

  userProfile: (userId?: string) => `user:profile:${userId || 'me'}`,

  /** Prefijo común de las claves de "Mis envíos"; permite invalidar todos los cuatrimestres. */
  mySubmissionsPrefix: 'submissions:v2:',

  mySubmissions: (userId?: string, term?: string) =>
    `submissions:v2:${userId || 'me'}:${term || 'default'}`,

  exerciseRankings: (guide: number, exercise: number) => `rankings:g${guide}-e${exercise}`,

  // Metrics — particionadas por cuatrimestre (S5): cambiar de term nunca sirve datos de otro
  metricsProgress: (term: string) => `metrics:progress:${term}`,
  metricsResolutionTime: (term: string) => `metrics:resolution-time:${term}`,
  metricsAttempts: (term: string) => `metrics:attempts:${term}`,
  metricsActiveStudents: (term: string) => `metrics:active-students:${term}`,
  metricsErrorRate: (term: string) => `metrics:error-rate:${term}`,
  metricsAtRisk: (term: string) => `metrics:at-risk:${term}`,
  metricsProgressDistribution: (term: string) => `metrics:progress-distribution:${term}`,
  metricsWeeklyActivity: (term: string) => `metrics:weekly-activity:${term}`,
  metricsCompletionMatrix: (term: string) => `metrics:completion-matrix:${term}`,

  adminGuides: 'admin:guides',
  adminSettings: 'admin:settings',
  publicSettings: 'settings:public',

  submissionResult: (guide: number, exercise: number) => `submission:result:g${guide}-e${exercise}`,
} as const;

// Tipos para TypeScript
export type CacheConfigKey = keyof typeof CACHE_CONFIG;
export type CacheKeysKey = keyof typeof CACHE_KEYS;

/** Argumentos que aceptan los generadores de claves: ids/cuatrimestres y números de guía/ejercicio. */
type CacheKeyArg = string | number | undefined;
type CacheKeyGenerator = (...args: CacheKeyArg[]) => string;

// Utilidades para trabajar con caché
export const CacheUtils = {
  /**
   * Obtiene el TTL para un tipo de caché específico
   */
  getTTL(key: CacheConfigKey): number {
    return CACHE_CONFIG[key];
  },

  /**
   * Genera la clave de caché para un endpoint específico
   */
  getCacheKey(key: CacheKeysKey, ...params: CacheKeyArg[]): string {
    const keyGenerator = CACHE_KEYS[key];

    if (typeof keyGenerator === 'function') {
      return (keyGenerator as CacheKeyGenerator)(...params);
    }

    return keyGenerator;
  },

  /**
   * Verifica si una clave de caché es válida
   */
  isValidCacheKey(key: string): boolean {
    return Object.values(CACHE_KEYS).some(keyGenerator => {
      if (typeof keyGenerator === 'function') {
        // Para funciones, verificamos si la clave coincide con algún patrón
        return key.includes((keyGenerator as CacheKeyGenerator)('').split(':')[0]);
      }
      return key === keyGenerator;
    });
  },
};
